/**
 * Feedback visual e medição da troca de tela.
 *
 * Entre o clique no menu e a tela nova pintar existem duas etapas sem nenhum
 * retorno visual: o guard de rota (`validateAuthRoute`) e o download do chunk
 * da tela (toda rota é `() => import(...)`). Em rede ruim isso são segundos de
 * tela parada, e o usuário conclui que o clique não funcionou e clica de novo.
 *
 * Este módulo guarda o estado que a `BarraProgressoNavegacao.vue` desenha e,
 * de quebra, cronometra cada fase para mandar as navegações lentas ao Sentry —
 * é o que vai dizer, com dado de produção, se a culpa é do chunk, do guard ou
 * do render.
 *
 * Mora aqui, e não na store Vuex, por dois motivos: a store não precisa saber
 * disso, e importar a store a partir do `router/index.js` (que é quem chama as
 * funções abaixo) tem histórico de ciclo de import neste projeto.
 */

import Vue from 'vue'
import * as Sentry from '@sentry/vue'

// Navegação com o chunk já em cache termina em poucos milissegundos; pintar a
// barra nesses casos vira um flash que polui mais do que informa. 250ms é o
// limiar em que o usuário começa a duvidar de que o clique pegou.
const ATRASO_PARA_EXIBIR_MS = 250

// Teto de segurança. Todos os desfechos conhecidos estão cobertos (`afterEach`,
// `onError` e o `next(false)` do guard), mas uma barra presa no topo da tela é
// pior do que não ter barra nenhuma.
const WATCHDOG_MS = 20000

// Cadência da animação. Curta o bastante para parecer viva, longa o bastante
// para não custar CPU em aparelho fraco — que é exatamente onde a navegação
// demora.
const INTERVALO_AVANCO_MS = 180

// A barra para em 90% e só completa quando a navegação realmente termina.
// Chegar a 100% antes da hora é mentir para o usuário justo na tela em que ele
// já está desconfiado.
const TETO_ANTES_DO_FIM = 90

// Fração do que falta consumida a cada passo: gera aproximação assintótica do
// teto, então a barra nunca "chega" nem trava visivelmente.
const FRACAO_DO_AVANCO = 0.12

// Espelha a transição de saída do CSS da barra. Se um dos dois mudar sem o
// outro, ou a barra some antes de completar ou fica um resíduo na tela.
const DURACAO_SAIDA_MS = 220

const LIMITE_LENTA_MS = 1500
const LIMITE_MUITO_LENTA_MS = 5000

// `captureMessage` gera um evento por navegação lenta. Numa rede ruim isso são
// dezenas de eventos na mesma sessão dizendo a mesma coisa; o breadcrumb (que
// não tem custo) continua registrando todas.
const MAX_CAPTURAS_POR_SESSAO = 3

/**
 * Estado lido pela `BarraProgressoNavegacao.vue`.
 *
 * `Vue.observable` em vez de um componente com estado próprio porque quem
 * dispara as transições é o router, não a árvore de componentes.
 */
export const estadoProgresso = Vue.observable({
    visivel: false,
    progresso: 0,
    concluindo: false,
})

const agora = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

let navegacaoAtual = null
let temporizadorExibir = null
let temporizadorAvanco = null
let temporizadorWatchdog = null
let temporizadorSaida = null
let capturasEnviadas = 0

const limparTemporizadores = () => {
    clearTimeout(temporizadorExibir)
    clearInterval(temporizadorAvanco)
    clearTimeout(temporizadorWatchdog)
    temporizadorExibir = null
    temporizadorAvanco = null
    temporizadorWatchdog = null
}

/**
 * Corta a animação de saída pela raiz e devolve a barra ao estado escondido.
 *
 * Precisa zerar `visivel` junto com o temporizador: cancelar só o temporizador
 * deixaria a barra parada a 100% na tela, sem ninguém para escondê-la (o
 * `concluirProgresso` sai cedo quando não há navegação em curso), e faria a
 * navegação seguinte nascer com a barra já visível, furando o limiar dos 250ms.
 */
const encerrarSaida = () => {
    clearTimeout(temporizadorSaida)
    temporizadorSaida = null
    estadoProgresso.visivel = false
    estadoProgresso.concluindo = false
    estadoProgresso.progresso = 0
}

const exibir = () => {
    estadoProgresso.visivel = true
    estadoProgresso.concluindo = false

    temporizadorAvanco = setInterval(() => {
        estadoProgresso.progresso += (TETO_ANTES_DO_FIM - estadoProgresso.progresso) * FRACAO_DO_AVANCO
    }, INTERVALO_AVANCO_MS)
}

const reportar = (navegacao, motivo) => {
    const total = Math.round(agora() - navegacao.iniciadoEm)

    if (total < LIMITE_LENTA_MS) return

    const dados = {
        de: navegacao.de,
        para: navegacao.para,
        ms_total: total,
        // Offsets desde o clique, não durações isoladas — a soma tem que bater
        // com o total, senão a leitura no Sentry engana.
        ms_guarda: navegacao.msFimDoGuard,
        ms_chunk: navegacao.msFimDoChunk,
        ms_render: navegacao.msFimDoChunk !== null ? total - navegacao.msFimDoChunk : null,
        redirecionamentos: navegacao.redirecionamentos,
        motivo,
    }

    Sentry.addBreadcrumb({
        category: 'navegacao',
        level: 'warning',
        message: `Navegação lenta: ${navegacao.nomeDestino} (${total}ms)`,
        data: dados,
    })

    if (total < LIMITE_MUITO_LENTA_MS) return
    if (capturasEnviadas >= MAX_CAPTURAS_POR_SESSAO) return

    capturasEnviadas += 1

    Sentry.captureMessage(`Navegação lenta: ${navegacao.nomeDestino}`, {
        level: 'warning',
        tags: {
            rota_destino: navegacao.nomeDestino,
            motivo_navegacao: motivo,
        },
        extra: dados,
    })
}

/**
 * Começa a cronometrar (e, passado o atraso, a mostrar a barra).
 *
 * Chamado no PRIMEIRO `beforeEach`, antes do guard de autenticação, para que o
 * relógio comece junto com o clique do usuário e não depois das checagens.
 */
export const iniciarProgresso = (to, from) => {
    // Carga inicial da página: o splash `#loading-bg` do `index.html` já cobre
    // essa espera (o `afterEach` é quem o esconde). Duas coisas girando ao
    // mesmo tempo só confundem, e o que o usuário reclamou foi da troca de
    // tela DENTRO do sistema. Mesma checagem que o guard usa para detectar
    // boot frio: na rota inicial do vue-router o `from` não tem nome.
    if (!from || !from.name) return

    if (navegacaoAtual) {
        // Redirecionamento dentro do guard (onboarding, "não autorizado",
        // restauração de sessão): é a MESMA intenção do usuário, então o
        // cronômetro e a barra já visível continuam de onde estavam em vez de
        // reiniciarem e esconderem o tempo já gasto.
        navegacaoAtual.para = to.path
        navegacaoAtual.nomeDestino = to.name || '(sem nome)'
        navegacaoAtual.redirecionamentos += 1
        return
    }

    // Só aqui, com uma navegação nova de fato começando: a barra da anterior
    // some na hora em vez de terminar a animação de saída por cima desta.
    encerrarSaida()

    navegacaoAtual = {
        iniciadoEm: agora(),
        // `path` e não `fullPath`: a query string não acrescenta nada ao
        // diagnóstico de lentidão e é o único pedaço da URL onde um dia pode
        // aparecer algo digitado pelo usuário. Não mandar para o Sentry o que
        // não se vai ler.
        de: from.path,
        para: to.path,
        nomeDestino: to.name || '(sem nome)',
        msFimDoGuard: null,
        msFimDoChunk: null,
        redirecionamentos: 0,
    }

    temporizadorExibir = setTimeout(exibir, ATRASO_PARA_EXIBIR_MS)
    temporizadorWatchdog = setTimeout(() => concluirProgresso('watchdog'), WATCHDOG_MS)
}

/**
 * Marca o fim do guard de autenticação.
 *
 * Chamado num `beforeEach` registrado DEPOIS do guard: hooks de navegação
 * rodam em ordem de registro, então este só executa quando o guard anterior
 * chamou `next()` sem redirecionar.
 */
export const marcarFimDoGuard = () => {
    if (!navegacaoAtual) return

    // Sobrescreve a cada passada, igual ao `marcarFimDoChunk`: numa cadeia de
    // redirecionamentos o guard roda de novo, e o que interessa medir é quanto
    // tempo o usuário passou preso na fase de guard no total — não quanto durou
    // a primeira das passadas. `redirecionamentos` acompanha o número no Sentry
    // para o dado não ser lido como se fosse uma passada só.
    navegacaoAtual.msFimDoGuard = Math.round(agora() - navegacaoAtual.iniciadoEm)
}

/**
 * Marca o fim do download/parse do chunk da tela.
 *
 * `beforeResolve` roda depois de o vue-router resolver os componentes assíncronos
 * da rota — é o ponto que separa "esperando a rede" de "montando a tela".
 */
export const marcarFimDoChunk = () => {
    if (!navegacaoAtual) return

    navegacaoAtual.msFimDoChunk = Math.round(agora() - navegacaoAtual.iniciadoEm)
}

/**
 * Encerra a navegação: completa a barra, reporta se foi lenta e zera o estado.
 *
 * Idempotente de propósito — os vários desfechos possíveis (`afterEach`,
 * `onError`, `next(false)`, watchdog) podem se sobrepor.
 */
export const concluirProgresso = (motivo = 'fim') => {
    if (!navegacaoAtual) return

    const navegacao = navegacaoAtual
    navegacaoAtual = null

    limparTemporizadores()
    reportar(navegacao, motivo)

    // Terminou antes do atraso de exibição: a barra nunca chegou a aparecer e
    // não há nada para animar de volta.
    if (!estadoProgresso.visivel) {
        estadoProgresso.progresso = 0
        return
    }

    estadoProgresso.progresso = 100
    estadoProgresso.concluindo = true

    temporizadorSaida = setTimeout(() => {
        temporizadorSaida = null
        estadoProgresso.visivel = false
        estadoProgresso.concluindo = false
        estadoProgresso.progresso = 0
    }, DURACAO_SAIDA_MS)
}
